import React, { useEffect, useMemo } from 'react';
import { useTranslation, Locale } from '@rbx/intl';
import { useIsAuthenticated } from '@modules/assistant/context/IsAuthenticatedProvider';
import { NavigationTypeRaw } from '@modules/utilities/pageBuild/types/NavigationRaw';
import { LinkToAssistant } from '@modules/assistant/components/LinkToAssistant';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import { List, makeStyles, OpenInNewIcon, Typography } from '@rbx/ui';
import {
  trackSearchAskAssistantClicked,
  trackSearchAskAssistantImpression,
} from '@modules/search/searchEvents';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import { SearchListItem } from './SearchListItem';
import AskAssistantListItem from './AskAssistantListItem';
import { SearchableContainer } from './hooks/useSearchNavigation';
import { TSearchResult } from './types/SearchResult';
import useSearchListStyles from './SearchList.styles';

interface AskAssistantProps {
  searchQuery: string;
  locale: Locale;
  searchSessionId: string;
  impressionRef: React.MutableRefObject<string | null>;
  searchResults: TSearchResult[];
}

const PHONETIC_MIN_LEN = 5;
const NONPHONETIC_MIN_LEN = 1;

const MIN_QUERY_LENGTH_PER_LOCALE: Record<Locale, number> = {
  [Locale.English]: PHONETIC_MIN_LEN,
  [Locale.Spanish]: PHONETIC_MIN_LEN,
  [Locale.French]: PHONETIC_MIN_LEN,
  [Locale.German]: PHONETIC_MIN_LEN,
  [Locale.Italian]: PHONETIC_MIN_LEN,
  [Locale.BrazilPortuguese]: PHONETIC_MIN_LEN,
  [Locale.Korean]: PHONETIC_MIN_LEN,
  [Locale.SimplifiedChinese]: NONPHONETIC_MIN_LEN, // non-phonetic
  [Locale.SimplifiedChineseJV]: NONPHONETIC_MIN_LEN, // non-phonetic
  [Locale.TraditionalChinese]: NONPHONETIC_MIN_LEN, // non-phonetic
  [Locale.Japanese]: NONPHONETIC_MIN_LEN, // non-phonetic
  [Locale.Russian]: PHONETIC_MIN_LEN,
  [Locale.Indonesian]: PHONETIC_MIN_LEN,
  [Locale.Polish]: PHONETIC_MIN_LEN,
  [Locale.Vietnamese]: PHONETIC_MIN_LEN,
  [Locale.Turkish]: PHONETIC_MIN_LEN,
  [Locale.Arabic]: PHONETIC_MIN_LEN,
  [Locale.Thai]: PHONETIC_MIN_LEN,
};

// remove all non-letter characters, unicode-aware
const cleanSearchQuery = (searchQuery: string) => searchQuery.replaceAll(/[^\p{L}]/gu, '').trim();

const isValidAssistantQuery = (locale: Locale, searchQuery: string) =>
  cleanSearchQuery(searchQuery).length >= MIN_QUERY_LENGTH_PER_LOCALE[locale];

const AskAssistant: React.FC<AskAssistantProps> = ({
  impressionRef,
  locale,
  searchQuery,
  searchSessionId,
  searchResults,
}) => {
  const { translate } = useTranslation();
  const { targetLocale } = useCurrentLocale();
  const { isAuthenticated } = useIsAuthenticated();
  const { classes } = useSearchListStyles();

  const isEnabled = isAuthenticated && isValidAssistantQuery(targetLocale, searchQuery);

  const prompt = useMemo(
    () => translate('Prompt.SearchToAssistant', { searchQuery: searchQuery.trim() }),
    [searchQuery, translate],
  );

  useEffect(() => {
    if (!isEnabled || !searchSessionId || impressionRef.current === searchSessionId) return;

    // eslint-disable-next-line no-param-reassign
    impressionRef.current = searchSessionId;
    trackSearchAskAssistantImpression({
      askAssistantLabel: prompt,
      locale,
      query: searchQuery,
      searchSessionId,
      searchResults,
    });
  }, [searchSessionId, isEnabled, impressionRef, prompt, locale, searchQuery, searchResults]);

  if (!isEnabled) {
    return null;
  }

  return (
    <React.Fragment>
      <div className={classes.sectionTitleContainer}>
        <Typography
          variant='tableHead'
          component='h2'
          color='primary'
          className={classes.sectionTitle}>
          {translate('Label.AskAssistant') || 'Ask Assistant'}
        </Typography>
      </div>
      <AskAssistantListItem
        prompt={prompt}
        label={prompt}
        locale={locale}
        searchQuery={searchQuery}
        searchSessionId={searchSessionId}
        searchResults={searchResults}
      />
    </React.Fragment>
  );
};

export default React.memo(AskAssistant);
